import type {
  AfterContentInit,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChildren,
  HostBinding,
  Input,
  QueryList,
} from '@angular/core';
import type { AbstractControl } from '@angular/forms';
import { FormControl } from '@angular/forms';
import { RepetitiveSubscription } from '@freelancer/decorators';
import { TimeUtils } from '@freelancer/time-utils';
import { IconColor } from '@freelancer/ui/icon';
import { FontColor, TextAlign, TextSize } from '@freelancer/ui/text';
import { Subscription } from 'rxjs';
import { startWith } from 'rxjs/operators';
import { CardListSelectColor, CardListSize } from './card-list.types';

@Component({
  selector: 'fl-card-list-item',
  template: `
    <div
      class="CardListItem"
      [class.IsSelected]="selectable && isSelected"
      [attr.aria-selected]="selectable ? isSelected : undefined"
      [attr.data-selectable]="selectable && !disabled"
      [attr.data-color]="color"
      [attr.role]="selectable ? 'option' : undefined"
      (click)="onClick()"
    >
      <fl-icon
        *ngIf="iconName"
        [name]="iconName"
        [color]="IconColor.INHERIT"
      ></fl-icon>

      <p class="text-color-inherit text-xxsmall text-center">
        <ng-content></ng-content>
      </p>
    </div>
  `,
  styleUrls: ['./card-list-item.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CardListItemComponent<T = boolean>
  implements OnChanges, OnInit, OnDestroy
{
  FontColor = FontColor;
  IconColor = IconColor;
  TextAlign = TextAlign;
  TextSize = TextSize;

  public color = CardListSelectColor.DEFAULT;
  public selectable = false;

  isSelected = false;

  @HostBinding('attr.data-size')
  public size = CardListSize.MID;

  @RepetitiveSubscription()
  private controlSubscription?: Subscription;

  @Input() control?: FormControl<
    [T] extends [undefined] ? boolean : T | undefined
  >;
  @Input() disabled = false;
  @Input() iconName?: string;
  /** If this is set, the control will be treated like a radio (set to value instead of `true`/`false`) */
  @Input() radioValue?: T;

  constructor(public changeDetectorRef: ChangeDetectorRef) {}

  ngOnChanges(changes: SimpleChanges): void {
    if ('control' in changes) {
      this.controlSubscription?.unsubscribe();
      this.controlSubscription = this.control?.valueChanges.subscribe(() => {
        this.updateSelectedState();
        this.changeDetectorRef.markForCheck();
      });
    }
  }

  ngOnInit(): void {
    this.updateSelectedState();
  }

  onClick(): void {
    if (this.control && this.control.enabled && !this.disabled) {
      // If we're given a radioValue, we treat the control like a radio
      if (this.radioValue !== undefined && this.isRadioControl(this.control)) {
        this.control.setValue(this.radioValue);
      } else if (this.isCheckboxControl(this.control)) {
        this.control.setValue(!this.control.value);
      }
    }
  }

  updateSelectedState(): void {
    // If we're given a radioValue, we treat the control like a radio
    if (this.control) {
      if (this.radioValue !== undefined && this.isRadioControl(this.control)) {
        this.isSelected = this.control.value === this.radioValue;
      } else if (this.isCheckboxControl(this.control)) {
        this.isSelected = this.control.value;
      }
    }
  }

  ngOnDestroy(): void {
    if (this.controlSubscription) {
      this.controlSubscription.unsubscribe();
    }
  }

  private isCheckboxControl(
    control: AbstractControl,
  ): control is FormControl<boolean> {
    return this.radioValue === undefined;
  }

  private isRadioControl(control: AbstractControl): control is FormControl<T> {
    return this.radioValue !== undefined;
  }
}

@Component({
  selector: 'fl-card-list',
  template: `<ng-content></ng-content>`,
  styleUrls: ['./card-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CardListComponent
  implements AfterContentInit, OnChanges, OnDestroy
{
  @Input() size = CardListSize.MID;
  @Input() selectable = false;
  @Input() selectColor = CardListSelectColor.DEFAULT;

  @HostBinding('attr.role') get role(): string | undefined {
    return this.selectable ? 'listbox' : undefined;
  }
  @HostBinding('attr.aria-orientation') get orientation(): string | undefined {
    return this.selectable ? 'horizontal' : undefined;
  }

  @ContentChildren(CardListItemComponent)
  cardListItemComponents: QueryList<CardListItemComponent>;

  private childListSubscription?: Subscription;

  constructor(private timeUtils: TimeUtils) {}

  ngOnChanges(): void {
    // apply properties to child items whenever the properties change.
    this.applyPropertiesToChildren();
  }

  ngAfterContentInit(): void {
    // apply properties to child items whenever the list of children changes
    this.childListSubscription = this.cardListItemComponents.changes
      .pipe(startWith(this.cardListItemComponents.toArray()))
      .subscribe(() => this.applyPropertiesToChildren());
  }

  ngOnDestroy(): void {
    if (this.childListSubscription) {
      this.childListSubscription.unsubscribe();
    }
  }

  applyPropertiesToChildren(): void {
    if (this.cardListItemComponents) {
      this.cardListItemComponents.forEach((component, index, arr) => {
        component.changeDetectorRef.detach();
        component.size = this.size;
        component.color = this.selectColor;
        component.selectable = this.selectable;
        component.changeDetectorRef.reattach();

        this.timeUtils.setTimeout(() =>
          component.changeDetectorRef.markForCheck(),
        );
      });
    }
  }
}
